import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Check, AlertCircle, Image, Calendar, User, Mail, FileText } from 'lucide-react';

const UploadPage = ({ navigateTo }) => {
  // Stati per il form
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    estimatedYear: '',
    userName: '',
    userEmail: ''
  });
  
  // Stati per il file
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Stati per l'upload
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  
  const fileInputRef = useRef(null);
  const dragRef = useRef(null);
  
  // Gestisce il cambio dei campi del form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Validazione file
  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!allowedTypes.includes(file.type)) {
      return 'Tipo di file non supportato. Usa JPG, PNG o WebP.';
    }
    
    if (file.size > maxSize) {
      return 'Il file è troppo grande. Massimo 10MB.';
    }
    
    return null;
  };
  
  // Gestisce la selezione del file
  const handleFileSelect = (file) => {
    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }
    
    setSelectedFile(file);
    setUploadError(null);
    
    // Crea preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  // Gestisce drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };
  
  // Gestisce il click per selezionare file
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };
  
  // Rimuove il file selezionato
  const removeFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Validazione form
  const validateForm = () => {
    if (!selectedFile) {
      return 'Seleziona una foto da caricare.';
    }
    
    if (!formData.title.trim()) {
      return 'Il titolo è obbligatorio.';
    }
    
    if (formData.title.length > 100) {
      return 'Il titolo deve essere lungo massimo 100 caratteri.';
    }
    
    if (formData.description.length > 500) {
      return 'La descrizione deve essere lunga massimo 500 caratteri.';
    }
    
    if (formData.estimatedYear && (formData.estimatedYear < 1990 || formData.estimatedYear > new Date().getFullYear())) {
      return 'L\'anno deve essere tra 1990 e oggi.';
    }
    
    return null;
  };
  
  // Gestisce l'invio del form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setUploadError(validationError);
      return;
    }
    
    setUploading(true);
    setUploadError(null);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('photo', selectedFile);
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('description', formData.description.trim());
      formDataToSend.append('estimatedYear', formData.estimatedYear);
      formDataToSend.append('userName', formData.userName.trim());
      formDataToSend.append('userEmail', formData.userEmail.trim());
      
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formDataToSend
      });
      
      const result = await response.json();
      
      if (result.success) {
        setUploadSuccess(true);
        
        // Reset form dopo successo
        setTimeout(() => {
          setFormData({
            title: '',
            description: '',
            estimatedYear: '',
            userName: '',
            userEmail: ''
          });
          setSelectedFile(null);
          setPreviewUrl(null);
          setUploadSuccess(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }, 3000);
        
      } else {
        setUploadError(result.error || 'Errore durante il caricamento');
      }
      
    } catch (error) {
      console.error('Errore upload:', error);
      setUploadError('Errore di connessione. Riprova più tardi.');
    } finally {
      setUploading(false);
    }
  };
  
  // Se upload completato con successo
  if (uploadSuccess) {
    return (
      <div className="container mx-auto px-4 py-6 text-amber-100">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-green-900/50 border border-green-700 rounded-lg p-8">
            <Check size={48} className="mx-auto text-green-400 mb-4" />
            <h2 className="text-xl font-bold text-green-300 mb-2">Foto Caricata!</h2>
            <p className="text-green-100 mb-4">
              La tua foto è stata caricata con successo e sarà visibile nella galleria dopo la moderazione.
            </p>
            <div className="space-y-2">
              <button
                onClick={() => navigateTo('upload')}
                className="w-full py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg transition-colors"
              >
                Carica un'altra foto
              </button>
              <button
                onClick={() => navigateTo('gallery')}
                className="w-full py-2 bg-stone-700 hover:bg-stone-600 text-amber-100 rounded-lg transition-colors"
              >
                Torna alla galleria
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-6 text-amber-100">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-amber-300 mb-2 flex items-center justify-center">
            <Camera size={28} className="mr-2" />
            Condividi i Tuoi Ricordi
          </h1>
          <p className="text-amber-200">
            Carica le tue foto del Celtic Druid e aiutaci a completare la storia del pub!
          </p>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Zona Upload Foto */}
          <div className="bg-stone-800 rounded-lg p-6">
            <label className="block text-sm font-medium text-amber-300 mb-4">
              <Image size={20} className="inline mr-2" />
              Foto *
            </label>
            
            {!selectedFile ? (
              <div
                ref={dragRef}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                  dragActive 
                    ? 'border-amber-400 bg-amber-900/20' 
                    : 'border-stone-600 hover:border-amber-500 hover:bg-stone-700/50'
                }`}
              >
                <Upload size={48} className="mx-auto text-stone-500 mb-4" />
                <p className="text-amber-200 mb-2">
                  Clicca per selezionare o trascina qui la tua foto
                </p>
                <p className="text-sm text-stone-400">
                  JPG, PNG o WebP - Massimo 10MB
                </p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full max-h-80 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={removeFile}
                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 transition-colors"
                >
                  <X size={20} />
                </button>
                <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-sm">
                  {selectedFile.name}
                </div>
              </div>
            )}
          </div>
          
          {/* Informazioni Foto */}
          <div className="bg-stone-800 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-medium text-amber-300 mb-4">Informazioni sulla Foto</h3>
            
            {/* Titolo */}
            <div>
              <label className="block text-sm font-medium text-amber-300 mb-2">
                <FileText size={16} className="inline mr-1" />
                Titolo *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Es: Serata karaoke del 2010"
                maxLength={100}
                className="w-full px-3 py-2 bg-stone-700 border border-stone-600 rounded-lg text-amber-100 placeholder-stone-400 focus:outline-none focus:border-amber-500"
                required
              />
              <div className="text-xs text-stone-400 mt-1">
                {formData.title.length}/100 caratteri
              </div>
            </div>
            
            {/* Descrizione */}
            <div>
              <label className="block text-sm font-medium text-amber-300 mb-2">
                Descrizione
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Racconta la storia di questa foto..."
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2 bg-stone-700 border border-stone-600 rounded-lg text-amber-100 placeholder-stone-400 focus:outline-none focus:border-amber-500 resize-none"
              />
              <div className="text-xs text-stone-400 mt-1">
                {formData.description.length}/500 caratteri
              </div>
            </div>
            
            {/* Anno stimato */}
            <div>
              <label className="block text-sm font-medium text-amber-300 mb-2">
                <Calendar size={16} className="inline mr-1" />
                Anno stimato
              </label>
              <input
                type="number"
                name="estimatedYear"
                value={formData.estimatedYear}
                onChange={handleInputChange}
                placeholder="2010"
                min="1990"
                max={new Date().getFullYear()}
                className="w-full px-3 py-2 bg-stone-700 border border-stone-600 rounded-lg text-amber-100 placeholder-stone-400 focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>
          
          {/* Informazioni Opzionali */}
          <div className="bg-stone-800 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-medium text-amber-300 mb-4">I Tuoi Dati (Opzionale)</h3>
            
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-amber-300 mb-2">
                <User size={16} className="inline mr-1" />
                Il tuo nome
              </label>
              <input
                type="text"
                name="userName"
                value={formData.userName}
                onChange={handleInputChange}
                placeholder="Mario Rossi"
                className="w-full px-3 py-2 bg-stone-700 border border-stone-600 rounded-lg text-amber-100 placeholder-stone-400 focus:outline-none focus:border-amber-500"
              />
            </div>
            
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-amber-300 mb-2">
                <Mail size={16} className="inline mr-1" />
                La tua email
              </label>
              <input
                type="email"
                name="userEmail"
                value={formData.userEmail}
                onChange={handleInputChange}
                placeholder="mario@example.com"
                className="w-full px-3 py-2 bg-stone-700 border border-stone-600 rounded-lg text-amber-100 placeholder-stone-400 focus:outline-none focus:border-amber-500"
              />
              <div className="text-xs text-stone-400 mt-1">
                Per contattarti se abbiamo domande sulla foto
              </div>
            </div>
          </div>
          
          {/* Messaggio di errore */}
          {uploadError && (
            <div className="bg-red-900/50 border border-red-700 text-red-100 p-4 rounded-lg flex items-start">
              <AlertCircle size={20} className="text-red-400 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Errore</h4>
                <p className="text-sm mt-1">{uploadError}</p>
              </div>
            </div>
          )}
          
          {/* Pulsanti */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigateTo('home')}
              className="flex-1 py-3 bg-stone-700 hover:bg-stone-600 text-amber-100 rounded-lg font-medium transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={uploading || !selectedFile}
              className={`flex-1 py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
                uploading || !selectedFile
                  ? 'bg-stone-600 text-stone-400 cursor-not-allowed'
                  : 'bg-green-700 hover:bg-green-600 text-white'
              }`}
            >
              {uploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Caricamento...
                </>
              ) : (
                <>
                  <Upload size={20} className="mr-2" />
                  Carica Foto
                </>
              )}
            </button>
          </div>
          
          {/* Nota privacy */}
          <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-4">
            <p className="text-amber-200 text-sm">
              <strong>Nota sulla Privacy:</strong> Le foto caricate saranno revisionate prima della pubblicazione. 
              Caricando una foto accetti che possa essere utilizzata nella galleria storica del Celtic Druid Irish Pub.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadPage;